import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, api } from './api';
import { AuthContext } from './auth-context';
import type { AuthUser } from './auth-context';

/**
 * Provee el estado de autenticación a la SPA.
 *
 * Al montar, intenta cargar `/auth/me`. Si el backend responde 401, deja
 * `user = null` y los componentes envueltos en `<RequireAuth>` redirigirán
 * a `/login`. Cualquier otro error de red mantiene `loading = true` un
 * instante y luego `user = null` (mejor degradar a login que dejar la app
 * en limbo).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api<AuthUser>('/auth/me')
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const u = await api<AuthUser>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST' });
    } catch (err) {
      // Si el backend dice 401 o similar, igualmente forzamos logout local.
      if (!(err instanceof ApiError) || err.status >= 500) throw err;
    }
    setUser(null);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const u = await api<AuthUser>('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    setUser(u);
  }, []);

  const acceptPrivacyPolicy = useCallback(async () => {
    const res = await api<{ acceptedAt: string }>('/me/privacy/accept', { method: 'POST' });
    setUser((prev) => (prev ? { ...prev, privacyPolicyAcceptedAt: res.acceptedAt } : prev));
  }, []);

  const markWhatsNewSeen = useCallback(async (version: string) => {
    await api<void>('/me/whats-new/seen', {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
    setUser((prev) => (prev ? { ...prev, whatsNewVersionSeen: version } : prev));
  }, []);

  const updateUser = useCallback((partial: Partial<AuthUser>) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        changePassword,
        acceptPrivacyPolicy,
        markWhatsNewSeen,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
